'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import type { Socket } from 'socket.io-client'
import io from 'socket.io-client'

interface AppVersion {
  version: string
  buildTime: string
}

export const useUpdateNotification = () => {
  const [currentVersion, setCurrentVersion] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('app-version') || ''
    }

    return ''
  })

  const [latestVersion, setLatestVersion] = useState<string>('')
  const [hasUpdate, setHasUpdate] = useState<boolean>(false)
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    socketRef.current = io(process.env.NEXT_PUBLIC_NEXT_SOCKET as string, {
      transports: ['websocket']
    })

    socketRef.current.on('connect', () => {
      socketRef.current?.emit('channel', 'APP')
    })

    socketRef.current.on('connect_error', err => {
      console.log('Connection error:', err.message)
    })

    socketRef.current.on('message', (data: any) => {
      checkForUpdate()
    })

    return () => {
      socketRef.current?.disconnect()
    }
  }, [])

  // Fungsi untuk mengambil versi saat ini dari server
  const fetchCurrentVersion = useCallback(async (): Promise<AppVersion | null> => {
    try {
      const response = await fetch('/api/app-version', {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch version')
      }

      const data: AppVersion = await response.json()

      return data
    } catch (error) {
      console.error('Error fetching app version:', error)

      return null
    }
  }, [])

  // Fungsi untuk mengecek update
  const checkForUpdate = useCallback(async () => {
    try {
      const versionData = await fetchCurrentVersion()

      if (versionData) {
        setLatestVersion(versionData.version)
        setLastChecked(new Date())
        console.log('Latest version from server:', versionData.version)

        // Ambil stored version dari localStorage
        const storedVersion = localStorage.getItem('app-version')

        console.log('Stored version:', storedVersion)
        console.log('Current version state:', currentVersion)

        // Jika belum ada di localStorage sama sekali (pertama kali)
        if (!storedVersion) {
          localStorage.setItem('app-version', versionData.version)
          setCurrentVersion(versionData.version)
          console.log('First time - saved version:', versionData.version)
        }

        // Jika ada perubahan versi
        else if (storedVersion !== versionData.version) {
          setHasUpdate(true)
          console.log('Update detected!', storedVersion, '->', versionData.version)
        }

        // Jika currentVersion state kosong tapi localStorage ada (after reload)
        else if (!currentVersion && storedVersion) {
          setCurrentVersion(storedVersion)
          console.log('Restored current version from localStorage:', storedVersion)
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error)
    }
  }, [currentVersion, fetchCurrentVersion])

  // Fungsi untuk reload aplikasi
  const reloadApp = useCallback(() => {
    // Update localStorage dengan versi terbaru
    if (latestVersion) {
      localStorage.setItem('app-version', latestVersion)
    }

    // Clear cache dan reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update()
        })
      })
    }

    // Reload page
    window.location.reload()
  }, [latestVersion])

  // Fungsi untuk dismiss notifikasi
  const dismissUpdate = useCallback(() => {
    setHasUpdate(false)

    // Update localStorage untuk menghindari notifikasi berulang
    if (latestVersion) {
      localStorage.setItem('app-version', latestVersion)
      setCurrentVersion(latestVersion)
    }
  }, [latestVersion])

  return {
    hasUpdate,
    currentVersion,
    latestVersion,
    isChecking,
    lastChecked,
    reloadApp,
    dismissUpdate,
    checkForUpdate
  }
}
